import { useEffect as useDrawerBaselineEffect } from "react";
import { useFormDirty } from "../../../../../../../shared/frontend/forms/useFormDirty";
import { cssSpacing, spacing } from "../../../app/design-system";
import type { FormInstance } from "../../../../../../../shared/frontend/ui";
import { Button,Descriptions,Divider,Form,InputNumber,Select,Typography } from "../../../../../../../shared/frontend/ui";
import type { ModalStaticFunctions } from "../../../../../../../shared/frontend/ui";
import type { DefaultOptionType } from "../../../../../../../shared/frontend/ui";
import {
CONTRIBUTION_STATUS_DUE,
CONTRIBUTION_STATUS_PAID
} from "../../../app/constants/contributionCalendarStatus";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import { formatDateOnly,formatMoney } from "../../../app/units/format";
import type { UnitsSettings } from "../../../app/units/units";
import { FormDrawer } from "../../../components/FormDrawer";
import type { ContributionCalendarRow } from "./personDetailTypes";
import type { Feedback } from "../../../../../../../shared/frontend/feedback/useFeedback";
import { ISO_DATE_LENGTH } from "../../../app/utils/isoDate";
import { MIN_PAYABLE_AMOUNT } from "./personAmountThresholds";

const DRAWER_WIDTH = 500;

const CONTRIBUTION_EDIT_FORM_ID = "person-contribution-edit-form";

export type ContributionEditFormValues = {
  amount?: number;
  paymentMethod?: number;
};

type ApiClient = {
  post: (url: string, body?: unknown) => Promise<unknown>;
  put: (url: string, body?: unknown) => Promise<unknown>;
};

type Props = {
  open: boolean;
  contribution: ContributionCalendarRow | null;
  form: FormInstance<ContributionEditFormValues>;
  saving: boolean;
  api: ApiClient;
  message: Feedback;
  modal: Pick<ModalStaticFunctions, "confirm">;
  text: I18nContextValue["text"];
  units: UnitsSettings;
  decimalSeparator: string;
  currencyCode: string;
  moneyPlaceholder: string;
  paymentMethodSelectOptions: DefaultOptionType[];
  onClose: () => void;
  onSavingChange: (saving: boolean) => void;
  onReload: () => void;
};

/** Voll bezahlt / nichts mehr offen → gar nicht bearbeitbar. */
function isContributionFullyPaid(contribution: ContributionCalendarRow): boolean {
  const statusPaid = Number(contribution.status || 0) === CONTRIBUTION_STATUS_PAID;
  const openAmount = Number(contribution.openAmount ?? (Number(contribution.amount || 0) - Number(contribution.paidAmount || 0)));
  return statusPaid || openAmount <= MIN_PAYABLE_AMOUNT;
}

/** Irgendeine Zahlung vorhanden (Teil-/Vollzahlung) → Betrag gesperrt, Löschen nicht erlaubt. */
function contributionHasPayment(contribution: ContributionCalendarRow): boolean {
  return Number(contribution.paidAmount || 0) > 0
    || Number(contribution.status || 0) === CONTRIBUTION_STATUS_PAID
    || !!contribution.paymentId;
}

export function PersonContributionEditDrawer({
  open,
  contribution,
  form,
  saving,
  api,
  message,
  modal,
  text,
  units,
  decimalSeparator,
  currencyCode,
  moneyPlaceholder,
  paymentMethodSelectOptions,
  onClose,
  onSavingChange,
  onReload
}: Props) {
  const { dirty: drawerDirty, resetDirty: resetDrawerDirty } = useFormDirty(form);
  const drawerOpen = open;
  useDrawerBaselineEffect(() => { if (drawerOpen) resetDrawerDirty(); }, [drawerOpen, resetDrawerDirty]);

  const canDeleteContribution = contribution
    ? !contributionHasPayment(contribution)
      && contribution.status !== CONTRIBUTION_STATUS_DUE
      && contribution.status !== CONTRIBUTION_STATUS_PAID
    : false;

  const handleDelete = async () => {
    if (!contribution) return;

    const dueDateFormatted = contribution.dueDate ? formatDateOnly(String(contribution.dueDate).slice(0, ISO_DATE_LENGTH), units) : "";
    const amountFormatted = formatMoney(Number(contribution.amount || 0), units);
    const contractName = contribution.contractName || text("Unknown");
    const ok = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: text("Delete contribution"),
        content: (
          <div>
            <Typography.Text>
              {text("Do you really want to delete this contribution?")}
            </Typography.Text>
            <Divider style={{ margin: cssSpacing(spacing.md, 0) }} />
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label={text("Contract component")}>
                {contractName}
              </Descriptions.Item>
              <Descriptions.Item label={text("Due date")}>
                {dueDateFormatted || "—"}
              </Descriptions.Item>
              <Descriptions.Item label={text("Amount")}>
                {amountFormatted}
              </Descriptions.Item>
              {contribution.paidAmount > 0 && (
                <Descriptions.Item label={text("Already paid")}>
                  {formatMoney(Number(contribution.paidAmount || 0), units)}
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>
        ),
        okText: text("Delete"),
        okButtonProps: { danger: true },
        cancelText: text("Cancel"),
        width: DRAWER_WIDTH,
        onOk: () => resolve(true),
        onCancel: () => resolve(false)
      });
    });
    if (!ok) return;

    onSavingChange(true);
    try {
      await api.post(`/api/v1/contribution-calendar/${encodeURIComponent(contribution.id)}/cancel`);
      message.success(text("Contribution deleted."));
      onClose();
      onReload();
    } catch {
      // Central API toast shows error.
    } finally {
      onSavingChange(false);
    }
  };

  const handleSave = async (values: ContributionEditFormValues) => {
    if (!contribution) return;

    // Bei (Teil-)Zahlung bleibt der Betrag gesperrt – dann nur die Zahlungsart senden.
    const amountIsLocked = Number(contribution.paidAmount || 0) > 0;
    onSavingChange(true);
    try {
      await api.put(`/api/v1/contribution-calendar/${encodeURIComponent(contribution.id)}`, {
        amount: amountIsLocked ? undefined : (values.amount != null ? Number(values.amount) : null),
        paymentMethod: values.paymentMethod != null ? Number(values.paymentMethod) : null
      });
      message.success(text("Contribution saved."));
      onClose();
      onReload();
    } catch {
      // Central API toast shows error.
    } finally {
      onSavingChange(false);
    }
  };

  const isFullyPaid = contribution ? isContributionFullyPaid(contribution) : false;
  // Bei Teilzahlung: Betrag gesperrt, Zahlungsart weiterhin änderbar.
  const amountLocked = contribution ? Number(contribution.paidAmount || 0) > 0 : false;

  return (
    <FormDrawer dirty={drawerDirty}
      title="Edit contribution"
      open={open}
      onCancel={onClose}
      formId={contribution && !isFullyPaid ? CONTRIBUTION_EDIT_FORM_ID : undefined}
      submitting={saving}
      hideSubmit={!contribution || isFullyPaid}
      extra={
        canDeleteContribution ? (
          <Button key="delete" danger disabled={saving} onClick={() => handleDelete()}>
            {text("Delete")}
          </Button>
        ) : null
      }
    >
      {contribution ? (
        isFullyPaid ? (
          <Typography.Paragraph type="secondary">
            {text("Already paid contributions cannot be edited")}
          </Typography.Paragraph>
        ) : (
          <Form
            id={CONTRIBUTION_EDIT_FORM_ID}
            form={form}
            layout="vertical"
            requiredMark={false}
            onFinish={(values) => handleSave(values)}
          >
            <Form.Item
              name="amount"
              label={text("Amount")}
              rules={[{ required: true, message: text("Amount is required") }]}
              extra={amountLocked ? <Typography.Text type="secondary">{text("Amount locked due to partial payment")}</Typography.Text> : undefined}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0.01}
                step={0.01}
                disabled={amountLocked}
                decimalSeparator={decimalSeparator}
                addonAfter={currencyCode}
                placeholder={moneyPlaceholder}
              />
            </Form.Item>
            <Form.Item
              name="paymentMethod"
              label={text("Payment method")}
              rules={[{ required: true, message: text("Payment method is required") }]}
            >
              <Select
                options={paymentMethodSelectOptions}
                placeholder={text("Select payment method…")}
              />
            </Form.Item>
          </Form>
        )
      ) : null}
    </FormDrawer>
  );
}
